import React from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { CheckOutlined } from '@mui/icons-material';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckoutElementsForm from '../payments/CheckoutElementsForm';
import { CheckoutPaymentMethod } from '../../types/appointments';
import useTerminalReader from '../../hooks/businesses/useTerminalReader';
import { LoadedUser } from '../../interfaces';
import useUser from '../../hooks/global/useUser';
import { cx } from '../../helpers/global';
import { getPaymentMethodFee } from '../../helpers/payments';

function labelMaker({
  method,
  selectedPaymentMethod,
  showFees,
  business,
  cardElementDisabled,
}: {
  method: CheckoutPaymentMethod;
  selectedPaymentMethod: CheckoutPaymentMethod;
  business: LoadedUser['business'];
  showFees?: boolean;
  cardElementDisabled?: boolean;
}) {
  const isChecked = selectedPaymentMethod === method;

  let src: string | React.FC = '';
  let labelName = '';

  switch (method) {
    case 'CREDIT':
      src = '/cards.svg';
      labelName = 'Send payment link to customer';
      break;
    case 'TERMINAL':
      src = '/pos.svg';
      labelName = 'Send to payment terminal';
      break;
    case 'MANUAL':
      src = CreditCardOutlined;
      labelName = 'Manually enter card';
      break;
    case 'CASH':
      src = '/cash.svg';
      labelName = 'Record cash or other payment (Zelle/Venmo etc.)';
      break;
    case 'FREE':
      src = CheckOutlined;
      labelName = 'Checkout for $0 (mark appointment completed)';
      break;
    case 'GIFT_CARD':
      src = CardGiftcardIcon;
      labelName = 'Record gift card payment';
      break;
    default:
      src = '';
      labelName = '';
  }

  const Icon = src as React.FC<React.SVGProps<SVGSVGElement>>;

  const feeId = `payment-method-fee-${method}`;
  const methodFeeRate = getPaymentMethodFee(method, business);

  return (
    <Stack
      gap={1}
      width="100%"
      sx={{
        [`#${feeId}`]: {
          opacity: isChecked ? 1 : undefined,
        },
        [`&:hover #${feeId}`]: {
          opacity: 1,
        },
      }}>
      <Box className="size-label" width="100%">
        {typeof src === 'string' ? (
          <img
            src={src}
            alt=""
            className="size-icon"
            style={{
              margin: 0,
            }}
          />
        ) : (
          <Icon
            width={30}
            height={30}
            style={{ width: '30px', height: '30px' }}
          />
        )}
        <Stack
          mr={0}
          width="100%"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className="size-label-text">
          <Typography variant="h5" component="p">
            {labelName}
          </Typography>
          {showFees && methodFeeRate > 0 && (
            <Typography
              id={feeId}
              variant="body2"
              component="p"
              sx={{
                opacity: 0,
                transition: 'opacity 0.25s ease-in-out',
              }}>
              {(methodFeeRate * 100).toFixed(2)}%
            </Typography>
          )}
        </Stack>
      </Box>
      {method === 'MANUAL' && selectedPaymentMethod === 'MANUAL' && (
        <CheckoutElementsForm disabled={cardElementDisabled} />
      )}
    </Stack>
  );
}

const PAYMENT_METHODS_ORDER: CheckoutPaymentMethod[] = [
  'TERMINAL',
  'CREDIT',
  'MANUAL',
  'CASH',
  'GIFT_CARD',
  'FREE',
];

interface Props {
  paymentMethods: CheckoutPaymentMethod[];
  selectedPaymentMethod: CheckoutPaymentMethod;
  onChange: (value: CheckoutPaymentMethod | '') => void;
  showFees?: boolean;
  disabled?: boolean;
  cardElementDisabled?: boolean;
}

function PaymentMethodsList({
  paymentMethods,
  selectedPaymentMethod,
  onChange,
  showFees = false,
  disabled = false,
  cardElementDisabled = false,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const { data: terminal } = useTerminalReader();

  const terminalIsOnline = React.useMemo(
    () => terminal?.status === 'online',
    [terminal],
  );

  const sortedPaymentMethods = paymentMethods?.sort(
    (a, b) =>
      PAYMENT_METHODS_ORDER.indexOf(a) - PAYMENT_METHODS_ORDER.indexOf(b),
  );

  return (
    <RadioGroup
      aria-labelledby="radio-payment-method"
      name="paymentMethod"
      id="payment-method-radio-group"
      value={selectedPaymentMethod}
      onChange={async (_event, value) => {
        onChange(value as CheckoutPaymentMethod);
      }}>
      {sortedPaymentMethods?.map(method => {
        if (method === 'TERMINAL' && !terminalIsOnline) return null;
        const isChecked = selectedPaymentMethod === method;
        return (
          <FormControlLabel
            key={method}
            disabled={disabled}
            value={method}
            control={
              <Radio
                inputProps={{ className: 'radio-payment-method' }}
                onClick={() => {
                  if (method === selectedPaymentMethod) {
                    onChange('');
                  }
                }}
              />
            }
            label={labelMaker({
              method,
              selectedPaymentMethod,
              showFees,
              business: user.business,
              cardElementDisabled,
            })}
            className={cx(
              'size-label',
              'md',
              isChecked && 'checked',
              disabled && 'disabled',
            )}
          />
        );
      })}
    </RadioGroup>
  );
}

export default PaymentMethodsList;
